import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface Claim {
    id: number;
    claimType: string;
    objectType: string;
}

export interface Widget {
    id: number;
    title: string;
}

export interface DataSource {
    id: number;
    description: string;
}

export interface Role {
    id: number;
    roleName: string;
    isAdminRole: boolean;
    claims: Claim[];
    widgets: Widget[];
    dataSources: DataSource[];
}

// API calls

export async function getRole(roleId: number): Promise<Role> {
    return rms2Fetch<Role>(`roles/${roleId}`);
}

export async function getAllClaims(): Promise<Claim[]> {
    const response = await rms2Fetch<{ claims: Claim[] }>('roles/claims');
    return response.claims;
}

export async function getAllWidgets(): Promise<Widget[]> {
    const response = await rms2Fetch<{ widgets: Widget[] }>('roles/widgets');
    return response.widgets;
}

export async function getAllDataSources(): Promise<DataSource[]> {
    const response = await rms2Fetch<{ dataSources: DataSource[] }>('roles/datasources');
    return response.dataSources;
}

export async function updateRole(role: Role): Promise<Role> {
    return rms2Fetch<Role>(`roles/${role.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            isAdminRole: role.isAdminRole,
            claimIds: role.claims.map(c => c.id),
            widgetIds: role.widgets.map(w => w.id),
            dataSourceIds: role.dataSources.map(d => d.id),
        }),
    });
}
